/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Tooltip } from 'antd'
import React from 'react'
import PermissionIcon from 'src/components/icons/PermissionIcon'
import PermissionUnselectedIcon from 'src/components/icons/PermissionUnselectedIcon'
import FollowingIcon from 'src/components/icons/FollowingIcon'
import StarIcon from 'src/components/icons/StarIcon'
import { MembershipType } from 'src/context/MyScheduleContext/types'

interface MembershipTypeFilterProps {
  selected: MembershipType
  onSelect: (type: MembershipType) => void
  disabled?: boolean
}

/**
 * MembershipTypeFilter component
 * Displays Permission/Following icon tabs for filtering by membership type
 * Styled to match TimePeriodFilter component
 */
const MembershipTypeFilter: React.FC<MembershipTypeFilterProps> = ({
  selected,
  onSelect,
  disabled = false,
}) => {
  const tabs: {
    value: MembershipType
    label: string
    Icon: typeof PermissionIcon | typeof StarIcon | typeof FollowingIcon
    UnselectedIcon?: typeof PermissionUnselectedIcon
  }[] = [
    {
      value: 'permission',
      label: 'Permission',
      Icon: PermissionIcon,
      UnselectedIcon: PermissionUnselectedIcon,
    },
    {
      value: 'following',
      label: 'Following',
      Icon: StarIcon,
      UnselectedIcon: FollowingIcon,
    },
  ]

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 0px;
        background-color: transparent;
        border: 1px solid ${colors.SECONDARY_200};
        border-radius: 8px;
        height: 40px;
        opacity: ${disabled ? 0.5 : 1};
        transition: opacity 0.2s ease;
        width: 146px;
        @media (max-width: 768px) {
          max-width: 100%;
          width: 100%;
        }
      `}
    >
      {tabs.map((tab, index) => {
        const isActive = selected === tab.value
        const { Icon, UnselectedIcon } = tab
        const IconComponent =
          !isActive && UnselectedIcon ? UnselectedIcon : Icon
        const isFirst = index === 0
        const isLast = index === tabs.length - 1

        return (
          <Tooltip key={tab.value} title={tab.label} placement='bottom'>
            <button
              type='button'
              disabled={disabled}
              onClick={() => !disabled && onSelect(tab.value)}
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 8px 24px;
                border-radius: ${isFirst
                  ? '8px 0 0 8px'
                  : isLast
                  ? '0 8px 8px 0'
                  : '0'};
                background-color: ${isActive
                  ? colors.SECONDARY_300
                  : 'transparent'};
                border: none;
                cursor: ${disabled ? 'not-allowed' : 'pointer'};
                transition: all 0.2s ease;

                ${!disabled &&
                css`
                  &:hover {
                    background-color: ${isActive
                      ? colors.SECONDARY_300
                      : colors.SECONDARY_200};
                  }

                  &:active {
                    transform: scale(0.98);
                  }
                  @media (max-width: 768px) {
                    flex: 1;
                  }
                `}
              `}
            >
              <IconComponent
                size={24}
                color={isActive ? colors.PRIMARY : colors.NEUTRAL_50}
              />
            </button>
          </Tooltip>
        )
      })}
    </div>
  )
}

export default MembershipTypeFilter
